<template>
  <li v-if="isLoading" class="shine-wrapper">
    <div class="shine"></div>
  </li>
  <b-dropdown-item @click="onClick" v-else>
    <div class="d-flex justify-content-between align-items-center">
      <span>{{ title }}</span>
      <i class="options-menu-icon" :class="entityIcon"></i>
    </div>
  </b-dropdown-item>
</template>

<script>
import { ENTITY_MAP, ENTITY_TYPE } from '@/constants/entities'
import service from '@/service'
import urlManager from '@/urlManager'
import showAPIError from '@/utils/showAPIError'
import showToast from '@/utils/showToast'
import { BDropdownItem } from 'bootstrap-vue'

/**
 * This gives a dropdown option for following/unfollowing a goal/kr/initiative
 */
export default {
  name: 'FollowOption',
  props: ['goalId', 'krId', 'initiativeId'],
  components: {
    BDropdownItem
  },
  data() {
    return {
      isLoading: false,
      isFollowing: null
    }
  },
  computed: {
    entityType() {
      if (this.initiativeId) {
        return ENTITY_TYPE.INITIATIVE
      } else if (this.krId) {
        return ENTITY_TYPE.KEY_RESULT
      } else {
        return ENTITY_TYPE.GOAL
      }
    },
    entity() {
      if (this.entityType === ENTITY_TYPE.INITIATIVE) {
        return this.$store.getters.initiativeById(this.initiativeId)
      } else if (this.entityType === ENTITY_TYPE.KEY_RESULT) {
        return this.$store.getters.keyResultById(this.krId)
      } else {
        return this.$store.getters.goalById(this.goalId)
      }
    },
    entityName() {
      const entity = ENTITY_MAP[this.entityType]
      return entity.shortName || entity.name
    },
    entityIcon() {
      if (this.entityType === ENTITY_TYPE.INITIATIVE) {
        return 'follow-initiative-icon'
      } else if (this.entityType === ENTITY_TYPE.KEY_RESULT) {
        return 'follow-kr-icon'
      } else {
        return 'follow-goal-icon'
      }
    },
    title() {
      const messages = [`Follow ${this.entityName}`, `Unfollow ${this.entityName}`]
      return messages[Number(this.isFollowing)]
    }
  },
  methods: {
    getSubmitSuccessMessage() {
      return this.isFollowing
        ? `You have unfollowed the ${this.entityName}`
        : `You are now following the ${this.entityName}`
    },
    getFollowActionUrl() {
      const urlMap = {
        [ENTITY_TYPE.GOAL]: urlManager.goalDetail({
          orgId: this.entity.organizationId,
          goalId: this.entity.id
        }),
        [ENTITY_TYPE.KEY_RESULT]: urlManager.KRDetail({
          orgId: this.entity.organizationId,
          krId: this.entity.id
        }),
        [ENTITY_TYPE.INITIATIVE]: urlManager.initiativeDetail({
          orgId: this.entity.organizationId,
          initiativeId: this.entity.id
        })
      }
      return urlMap[this.entityType]
    },
    async onClick() {
      try {
        await service.client.post(this.getFollowActionUrl(), {
          action: this.isFollowing ? 'unfollow' : 'follow'
        })
        showToast(this.getSubmitSuccessMessage())
      } catch (e) {
        showAPIError(e)
      }
    },
    getUserPropUrl() {
      const urlMap = {
        [ENTITY_TYPE.GOAL]: urlManager.goalUserProps,
        [ENTITY_TYPE.KEY_RESULT]: urlManager.krUserProps,
        [ENTITY_TYPE.INITIATIVE]: urlManager.initiativeUserProps
      }
      return urlMap[this.entityType]
    },
    async fetchUserProps() {
      this.isLoading = true
      const url = this.getUserPropUrl()
      try {
        const { data: userProps } = await service.client.get(
          url({ orgId: this.entity.organizationId, entityId: this.entity.id })
        )
        this.isFollowing = userProps.isFollowing
      } catch (e) {
        showAPIError(e)
      }
      this.isLoading = false
    }
  },
  mounted() {
    this.fetchUserProps()
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/components/_optionsMenu.scss';

.shine {
  width: 120px;
  border-radius: 4px;
  height: 1rem;
}

.shine-wrapper {
  padding: 4px 24px;
}

i {
  @include dimension(20px, 20px);
}
</style>
